import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useSessionPresence } from "@/hooks/useSessionPresence";
import { useSessionParticipants } from "@/hooks/useSessionParticipants";
import { useRealtimeScenarios } from "@/hooks/useRealtimeScenarios";
import { Loader2, Menu, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { UserMenu } from "@/components/UserMenu";
import { CollaborationBar } from "@/components/session/CollaborationBar";
import { useSessionSteps } from "@/hooks/useSessionSteps";
import { StrategicOptionsOverview } from "@/components/session/StrategicOptionsOverview";
import { PhaseNavigationDialog } from "@/components/session/PhaseNavigationDialog";
import { GlobalPhaseTimeline } from "@/components/session/GlobalPhaseTimeline";
import { ParticipantsPanel } from "@/components/session/ParticipantsPanel";
import quentariLogo from "@/assets/quentari-logo.svg";

const Phase2 = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { activeUsers, updatePresence } = useSessionPresence(sessionId, user?.id || null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [showBackDialog, setShowBackDialog] = useState(false);
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const { participants } = useSessionParticipants(sessionId);
  const { steps } = useSessionSteps(sessionId);
  
  // Reference to StrategicOptionsOverview's loadOptions for realtime refresh
  const strategicOptionsRef = useRef<{ reload: () => void } | null>(null);
  
  // Realtime sync for scenarios
  useRealtimeScenarios(sessionId, user?.id || null, () => {
    strategicOptionsRef.current?.reload();
  });
  
  // Broadcast subphase on mount
  useEffect(() => {
    updatePresence({ current_subphase: "2.0" });
  }, [updatePresence]);
  useEffect(() => {
    loadSessionData();
  }, [sessionId]);

  const loadSessionData = async () => {
    if (!sessionId) {
      navigate("/dashboard");
      return;
    }

    try {
      const { data: session, error } = await supabase
        .from("decision_sessions")
        .select("phase, workspace_id")
        .eq("id", sessionId)
        .single();

      if (error) throw error;

      if (session) {
        setWorkspaceId(session.workspace_id);
      }
      if (!session) {
        toast({
          title: "Session nicht gefunden",
          description: "Die Session konnte nicht geladen werden.",
          variant: "destructive",
        });
        navigate("/dashboard");
        return;
      }

      // Redirect to correct phase if not in Phase 2
      if (session.phase === 3) {
        navigate(`/app/session/${sessionId}/phase3`);
        return;
      } else if (session.phase !== 2) {
        toast({
          title: "Falsche Phase",
          description: "Diese Session ist noch nicht in Phase 2.",
          variant: "destructive",
        });
        navigate(`/app/session/${sessionId}`);
        return;
      }

      setIsLoading(false);
    } catch (error: any) {
      console.error("Error loading session:", error);
      toast({
        title: "Fehler",
        description: "Die Session-Daten konnten nicht geladen werden.",
        variant: "destructive",
      });
      navigate("/dashboard");
    }
  };

  const handleContinue = () => {
    navigate(`/app/session/${sessionId}/phase3`);
  };

  const [targetPhase, setTargetPhase] = useState<number | null>(null);

  const handlePhaseNavigation = (phase: number) => {
    setTargetPhase(phase);
    setShowBackDialog(true);
  };

  const handleConfirmNavigation = async () => {
    if (!sessionId || targetPhase === null) return;
    
    try {
      if (targetPhase === 1) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 1, subphase: "1.4" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}`);
      } else if (targetPhase === 3) {
        // Guard: ensure at least 2 scenarios exist before allowing Phase 3
        const { count } = await supabase
          .from("scenarios")
          .select("id", { count: "exact", head: true })
          .eq("session_id", sessionId);
        
        if (!count || count < 2) {
          toast({
            title: "Nicht genug Optionen",
            description: "Es müssen mindestens 2 strategische Optionen erstellt werden, bevor Phase 3 gestartet werden kann.",
            variant: "destructive",
          });
          setShowBackDialog(false);
          return;
        }
        
        await supabase
          .from("decision_sessions")
          .update({ phase: 3, subphase: "3.0" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}/phase3`);
      } else if (targetPhase === 4) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 3, subphase: "3.4" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}/report`);
      }
    } catch (error: any) {
      console.error("Error navigating to phase:", error);
      toast({
        title: "Fehler",
        description: "Navigation fehlgeschlagen.",
        variant: "destructive",
      });
    }
  };

  const getPhaseLabel = (phase: number) => {
    const labels: Record<number, string> = {
      1: "Phase 1 – Analyse",
      2: "Phase 2 – Optionen",
      3: "Phase 3 – Bewertung",
      4: "Report"
    };
    return labels[phase] || `Phase ${phase}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div 
            className="absolute top-0 left-0 w-full h-full opacity-[0.02]"
            style={{
              backgroundImage: `
                radial-gradient(ellipse 60% 40% at 10% 20%, hsl(var(--primary)) 0%, transparent 50%),
                radial-gradient(ellipse 50% 50% at 90% 80%, hsl(var(--primary)) 0%, transparent 50%)
              `,
            }}
          />
        </div>
        <div className="text-center relative z-10">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mx-auto mb-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
          <p className="text-muted-foreground">Phase 2 wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div 
          className="absolute top-0 left-0 w-full h-full opacity-[0.02]"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 60% 40% at 10% 20%, hsl(var(--primary)) 0%, transparent 50%),
              radial-gradient(ellipse 50% 50% at 90% 80%, hsl(var(--primary)) 0%, transparent 50%)
            `,
          }}
        />
        <div 
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="container flex h-16 items-center justify-between max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 md:gap-4">
            <img 
              src={quentariLogo} 
              alt="Quentari" 
              className="h-8 w-auto cursor-pointer transition-transform duration-300 hover:scale-105" 
              onClick={() => navigate('/dashboard')}
            />
            <div className="h-6 w-px bg-border hidden sm:block" />
            <GlobalPhaseTimeline
              currentPhase={2}
              sessionId={sessionId!}
              onPhaseClick={handlePhaseNavigation}
            />
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Button variant="ghost" size="sm" className="gap-2 hover:bg-primary/10 hover:text-primary" onClick={() => setIsParticipantsOpen(true)}>
              <Users className="h-4 w-4" />
              Teilnehmer ({participants.length})
            </Button>
            <CollaborationBar users={activeUsers} steps={steps} currentSubphase="2.0" />
            <UserMenu />
          </div>

          {/* Mobile Navigation */}
          <div className="flex md:hidden items-center gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-primary/10 hover:text-primary">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] border-border/50">
                <div className="flex flex-col gap-6 mt-6">
                  <div className="p-4 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
                    <span className="text-sm font-medium">Phasen-Navigation</span>
                    <div className="mt-3">
                      <GlobalPhaseTimeline
                        currentPhase={2}
                        sessionId={sessionId!}
                        onPhaseClick={handlePhaseNavigation}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Button variant="ghost" size="sm" className="w-full justify-start gap-2" onClick={() => setIsParticipantsOpen(true)}>
                      <Users className="h-4 w-4" />
                      Teilnehmer ({participants.length})
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <span className="text-sm font-medium">Aktive Nutzer</span>
                    <CollaborationBar users={activeUsers} steps={steps} currentSubphase="2.0" />
                  </div>
                  <div className="border-t border-border/50 pt-4">
                    <UserMenu />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="container max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12 relative z-10">
        <StrategicOptionsOverview 
          sessionId={sessionId!} 
          onContinue={handleContinue} 
        />
      </main>

      {/* Phase Navigation Dialog */}
      <PhaseNavigationDialog
        open={showBackDialog}
        onOpenChange={setShowBackDialog}
        onConfirm={handleConfirmNavigation}
        fromPhase="Phase 2 – Optionen"
        toPhase={getPhaseLabel(targetPhase || 1)}
      />

      <ParticipantsPanel
        sessionId={sessionId!}
        workspaceId={workspaceId}
        open={isParticipantsOpen}
        onOpenChange={setIsParticipantsOpen}
      />
    </div>
  );
};

export default Phase2;
